import { escapeHtml } from "./html-escape.js";

const STALE_MS = 18_000;
const HEARTBEAT_MS = 7_000;

function initials(name) {
  return String(name || "N").split(/\s+/).filter(Boolean).map(part => part[0]).join("").slice(0, 2).toUpperCase() || "N";
}

export class CollaborationService {
  constructor(platform) {
    this.platform = platform;
    this.client = platform.client;
    this.profile = platform.profile;
    this.workspaceId = platform.workspaceId;
    this.channel = null;
    this.remote = new Map();
    this.current = null;
    this.sessionId = crypto.randomUUID();
    this.cleanupTimer = null;
    this.heartbeatTimer = null;
    this.observer = null;
    this.inputTimer = null;
  }

  async start() {
    if (!this.client || this.platform.mode !== "cloud") return;
    this.channel = this.client.channel(`naysor-collaboration:${this.workspaceId}`, {
      config: { broadcast: { self: false, ack: false } }
    });
    this.channel.on("broadcast", { event: "editing" }, message => this.receive(message.payload || {}));
    await new Promise(resolve => this.channel.subscribe(status => {
      if (["SUBSCRIBED", "CHANNEL_ERROR", "TIMED_OUT"].includes(status)) resolve();
    }));
    document.addEventListener("focusin", event => this.onFocus(event), true);
    document.addEventListener("input", event => this.onInput(event), true);
    document.addEventListener("focusout", event => this.onBlur(event), true);
    window.addEventListener("beforeunload", () => this.stopCurrent());
    this.cleanupTimer = setInterval(() => this.prune(), 4000);
    this.observer = new MutationObserver(records => {
      const structural = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => {
        if (!(node instanceof Element)) return true;
        return !node.matches(".collab-editor-badge,[data-google-generated='true']");
      }));
      if (structural) this.render();
    });
    this.observer.observe(document.getElementById("appShell") || document.body, { subtree: true, childList: true });
  }

  inferEntity(target) {
    const form = target.closest("form[data-form]");
    if (form) {
      const type = form.dataset.form;
      if (["profile","password","invite"].includes(type)) return null;
      const original = form.elements.originalId?.value || form.elements.id?.value || "new";
      return { type, id: original, field: target.name || target.id || "record", label: target.closest(".field")?.querySelector("label")?.textContent?.trim() || target.name || "field" };
    }
    if (target.dataset.action === "gate-evidence") return { type: "gate", id: target.dataset.gate, field: `check:${target.dataset.check}`, label: "Gate evidence" };
    return null;
  }

  onFocus(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return;
    const entity = this.inferEntity(target);
    if (!entity) return;
    this.startCurrent(entity, target);
  }

  onInput(event) {
    if (!this.current || event.target !== this.current.element) return;
    clearTimeout(this.inputTimer);
    this.inputTimer = setTimeout(() => this.send(true), 220);
  }

  onBlur(event) {
    if (!this.current || event.target !== this.current.element) return;
    setTimeout(() => {
      if (document.activeElement !== this.current?.element) this.stopCurrent();
    }, 80);
  }

  startCurrent(entity, element) {
    if (this.current && (this.current.type !== entity.type || this.current.id !== entity.id || this.current.field !== entity.field)) this.stopCurrent();
    this.current = { ...entity, element };
    this.send(true);
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => this.send(true), HEARTBEAT_MS);
    const remoteKey = `${entity.type}:${entity.id}:${entity.field}`;
    if (this.remote.has(remoteKey)) {
      element.closest(".field")?.classList.add("remote-editor-warning");
      window.dispatchEvent(new CustomEvent("naysor:collaboration-warning", { detail: this.remote.get(remoteKey) }));
    }
  }

  stopCurrent() {
    if (!this.current) return;
    this.send(false);
    this.current.element?.closest(".field")?.classList.remove("remote-editor-warning");
    this.current = null;
    clearTimeout(this.inputTimer);
    clearInterval(this.heartbeatTimer);
  }

  send(editing) {
    if (!this.channel || !this.current) return;
    this.channel.send({
      type: "broadcast",
      event: "editing",
      payload: {
        editing,
        entityType: this.current.type,
        entityId: this.current.id,
        field: this.current.field,
        fieldLabel: this.current.label,
        view: window.NAYSOR_APP?.getView?.() || "",
        userId: this.profile.id,
        userName: this.profile.full_name || this.profile.email,
        avatarUrl: this.profile.avatar_url || "",
        sessionId: this.sessionId,
        at: new Date().toISOString()
      }
    });
  }

  receive(payload) {
    if (!payload?.userId || payload.userId === this.profile.id) return;
    const key = `${payload.entityType}:${payload.entityId}:${payload.field}`;
    if (!payload.editing) this.remote.delete(key);
    else this.remote.set(key, { ...payload, receivedAt: Date.now() });
    this.render();
    window.dispatchEvent(new CustomEvent("naysor:editing-presence", { detail: [...this.remote.values()] }));
  }

  prune() {
    const now = Date.now();
    let changed = false;
    for (const [key, value] of this.remote) {
      if (now - value.receivedAt > STALE_MS) { this.remote.delete(key); changed = true; }
    }
    if (changed) this.render();
  }

  render() {
    const observer = this.observer;
    observer?.disconnect();
    document.querySelectorAll(".collab-editor-badge[data-generated='true']").forEach(node => node.remove());
    document.querySelectorAll(".collab-editing-target").forEach(node => node.classList.remove("collab-editing-target"));
    const grouped = new Map();
    for (const editor of this.remote.values()) {
      const entityKey = `${editor.entityType}:${editor.entityId}`;
      const values = grouped.get(entityKey) || [];
      values.push(editor);
      grouped.set(entityKey, values);

      const form = [...document.querySelectorAll(`form[data-form="${CSS.escape(editor.entityType)}"]`)].find(item => {
        const id = item.elements.originalId?.value || item.elements.id?.value || "new";
        return id === editor.entityId;
      });
      const field = form?.elements?.[editor.field];
      if (field) {
        const wrapper = field.closest(".field") || field.parentElement;
        wrapper?.classList.add("collab-editing-target");
        wrapper?.insertAdjacentHTML("beforeend", this.badge(editor, true));
      }
    }
    for (const [entityKey, editors] of grouped) {
      const [type, id] = entityKey.split(":");
      const candidates = [...document.querySelectorAll(`[data-id="${CSS.escape(id)}"]`)].filter(node => {
        const action = node.dataset.action || "";
        return action.includes(type) || (type === "phase" && action === "select-phase") || (type === "gate" && action === "select-gate");
      });
      const target = candidates[0];
      if (target) {
        target.classList.add("collab-editing-target");
        const unique = [...new Map(editors.map(editor => [editor.userId, editor])).values()].slice(0, 3);
        target.insertAdjacentHTML("beforeend", `<span class="collab-editor-stack collab-editor-badge" data-generated="true">${unique.map(editor => this.avatar(editor)).join("")}</span>`);
      }
    }
    observer?.observe(document.getElementById("appShell") || document.body, { subtree: true, childList: true });
  }

  avatar(editor) {
    const image = editor.avatarUrl ? `style="background-image:url('${escapeHtml(editor.avatarUrl).replaceAll("'", "%27")}')"` : "";
    return `<span class="collab-editor-avatar${image ? " has-image" : ""}" ${image} title="${escapeHtml(editor.userName)} · ${escapeHtml(editor.fieldLabel)} 수정 중">${image ? "" : escapeHtml(initials(editor.userName))}</span>`;
  }

  badge(editor, field = false) {
    return `<span class="collab-editor-badge${field ? " field-badge" : ""}" data-generated="true">${this.avatar(editor)}<span>${escapeHtml(editor.userName)} 수정 중</span></span>`;
  }

  async stop() {
    this.stopCurrent();
    clearInterval(this.cleanupTimer);
    clearInterval(this.heartbeatTimer);
    this.observer?.disconnect();
    if (this.channel) await this.client.removeChannel(this.channel);
  }
}
